import React, { useEffect, useState } from "react"
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Chip,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import BusinessIcon from "@mui/icons-material/Business"
import StorefrontIcon from "@mui/icons-material/Storefront"
import PointOfSaleIcon from "@mui/icons-material/PointOfSale"
import InventoryIcon from "@mui/icons-material/Inventory"
import PeopleIcon from "@mui/icons-material/People"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import {
    getMyAssignments,
    CompanyAssignment,
} from "../../services/assignmentService"
import apiClient from "../../services/apiClient"
import { useTranslation } from "react-i18next"

interface CompanyStats {
    company: CompanyAssignment
    outletCount: number
    registerCount: number
    userCount: number
    itemCount: number
}

function AdminDashboard() {
    const { t } = useTranslation()
    const [companyStats, setCompanyStats] = useState<CompanyStats[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false

        async function load() {
            try {
                const assignments = await getMyAssignments()
                const companies = assignments.companyAssignments

                const stats: CompanyStats[] = []

                for (const company of companies) {
                    let outletCount = 0
                    let registerCount = 0
                    let userCount = 0
                    let itemCount = 0

                    try {
                        const outletsRes = await apiClient.get(
                            `/api/outlets?company_id=${company.company_id}`
                        )
                        const outlets = outletsRes.outlets || []
                        outletCount = outlets.length

                        for (const outlet of outlets) {
                            try {
                                const regRes = await apiClient.get(
                                    `/api/registers?outlet_id=${outlet.id}`
                                )
                                registerCount += (regRes.registers || []).length
                            } catch {
                                // ignore
                            }
                        }
                    } catch {
                        // ignore
                    }

                    try {
                        const usersRes = await apiClient.get(
                            `/api/companies/${company.company_id}/users`
                        )
                        userCount = (usersRes.users || []).length
                    } catch {
                        // ignore
                    }

                    try {
                        const itemsRes = await apiClient.get(
                            `/api/companies/${company.company_id}/items`
                        )
                        itemCount = (itemsRes.items || []).length
                    } catch {
                        // ignore
                    }

                    stats.push({
                        company,
                        outletCount,
                        registerCount,
                        userCount,
                        itemCount,
                    })
                }

                if (!cancelled) {
                    setCompanyStats(stats)
                }
            } catch (e: any) {
                if (!cancelled)
                    setError(e.message || "Failed to load dashboard")
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        load()
        return () => {
            cancelled = true
        }
    }, [])

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        )
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>
    }

    const totalOutlets = companyStats.reduce((acc, s) => acc + s.outletCount, 0)
    const totalRegisters = companyStats.reduce(
        (acc, s) => acc + s.registerCount,
        0
    )
    const totalUsers = companyStats.reduce((acc, s) => acc + s.userCount, 0)
    const totalItems = companyStats.reduce((acc, s) => acc + s.itemCount, 0)

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                    {t("dashboard.admin.title")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t("dashboard.admin.subtitle")}
                </Typography>
            </Box>

            {/* Global summary cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            textAlign: "center",
                        }}
                    >
                        <BusinessIcon
                            sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
                        />
                        <Typography variant="h3" fontWeight={700}>
                            {companyStats.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("dashboard.admin.companies")}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            textAlign: "center",
                        }}
                    >
                        <StorefrontIcon
                            sx={{
                                fontSize: 40,
                                color: "secondary.main",
                                mb: 1,
                            }}
                        />
                        <Typography variant="h3" fontWeight={700}>
                            {totalOutlets}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("dashboard.admin.outlets")}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            textAlign: "center",
                        }}
                    >
                        <PointOfSaleIcon
                            sx={{ fontSize: 40, color: "success.main", mb: 1 }}
                        />
                        <Typography variant="h3" fontWeight={700}>
                            {totalRegisters}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("dashboard.admin.registers")}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            textAlign: "center",
                        }}
                    >
                        <PeopleIcon
                            sx={{ fontSize: 40, color: "warning.main", mb: 1 }}
                        />
                        <Typography variant="h3" fontWeight={700}>
                            {totalUsers}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("dashboard.admin.users")}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Additional stats row */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            textAlign: "center",
                        }}
                    >
                        <InventoryIcon
                            sx={{ fontSize: 40, color: "info.main", mb: 1 }}
                        />
                        <Typography variant="h3" fontWeight={700}>
                            {totalItems}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("dashboard.admin.items")}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            textAlign: "center",
                        }}
                    >
                        <TrendingUpIcon
                            sx={{ fontSize: 40, color: "success.main", mb: 1 }}
                        />
                        <Typography variant="h3" fontWeight={700}>
                            &mdash;
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("dashboard.admin.revenueToday")}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Company details */}
            <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
                {t("dashboard.admin.companies")}
            </Typography>
            <Grid container spacing={3}>
                {companyStats.map((stat) => (
                    <Grid item xs={12} key={stat.company.company_id}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: "divider",
                                "&:hover": {
                                    borderColor: "primary.main",
                                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                },
                                transition: "all 0.2s",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <BusinessIcon
                                    sx={{
                                        color: "primary.main",
                                        mr: 1.5,
                                        fontSize: 28,
                                    }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight={600}>
                                        {stat.company.trade_name ||
                                            stat.company.legal_name}
                                    </Typography>
                                    {stat.company.trade_name && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {stat.company.legal_name}
                                        </Typography>
                                    )}
                                </Box>
                                <Chip
                                    label={stat.company.role}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                />
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("dashboard.admin.outlets")}
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                        {stat.outletCount}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("dashboard.admin.registers")}
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                        {stat.registerCount}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("dashboard.admin.users")}
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                        {stat.userCount}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("dashboard.admin.items")}
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                        {stat.itemCount}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {companyStats.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {t("dashboard.admin.noCompany")}
                </Alert>
            )}
        </Box>
    )
}

export default AdminDashboard
